import AppCheckbox from '@nxsys/components/AppCheckbox';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import {
  defaultHolidayPayCalcOptions,
  getHolidayPaymentMethodOptions,
  getUmbrellaHolidayPaymentMethodOptions,
  leaveYearStartDateOptions,
  paymentModels,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import {
  HolidayMethodEnum,
  PAYMENT_MODEL_ENUM,
} from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { Option } from '@nxsys/types/models/Common';
import {
  getAppConfig,
  PAYMENT_MODEL_TEMPLATES,
  PAYSLIP_TEMPLATES,
} from '@nxsys/utils';
import {
  getDemoCisTemplateLayout,
  getDemoTemplateLayout,
  RCT_DEFAULT_DEMO_LAYOUT,
  SELF_CIS_DEFAULT_DEMO_LAYOUT,
} from '@nxsys/constants/templateFiles';
import { Col, Divider, Form, Row } from 'antd';
import { some } from 'lodash';
import React, { Ref, useEffect, useImperativeHandle, useMemo } from 'react';
import AppPercentageInput from '@nxsys/components/AppPercentageInput';

export type PaymentModelFormValues = {
  holidayPayCalculationRule: Option<string> | null;
  payePayslipTemplate: Option<string> | null;
  enableHolidayPay: boolean;
  payeHolidayPayRate: string;
  holidayPayMethod: Option<string> | null;
  includeInTimesheetRate: boolean | null;
  splitSalaryAndBonus: boolean;
  umbrellaPayslipTemplate: Option<string> | null;
  enableHolidayPayUmbrella: boolean;
  umbrellaHolidayPayRate: string;
  umbrellaHolidayPayMethod: Option<string> | null;
  reclaimAppLevyUmbrella: boolean;
  accrueEmploymentCosts: boolean;
  hybridPayePayslipTemplate: Option<string> | null;
  cisPayslipTemplate: Option<string> | null;
  reclaimAppLevyHybrid: boolean;
  hybridPayslipTemplate: Option<string> | null;
  // NX-6284: Ireland-only leave year start, default 1 April. Encoded as
  // "day-month" (toLeaveYearStart/fromLeaveYearStart) so one dropdown
  // carries both numbers the API still wants.
  holidayLeaveYearStart: Option<string> | null;
  taxFreePaymentDesc: string;
  payeETHPayslipTemplate: Option<string> | null;
  payeFthMarginAdjustment: boolean;
  includedPaye: boolean;
  includedUmbrella: boolean;
  includedHybrid: boolean;
  includedOPayehybrid: boolean;
  includedOPayeFth: boolean;
  includedCis: boolean;
};

export type PaymentModelFormRef = {
  submit: () => void;
};

type PaymentModelFormProps = {
  formRef?: Ref<PaymentModelFormRef>;
  onSubmit?: (values: PaymentModelFormValues) => void;
  defaultValues?: Partial<PaymentModelFormValues>;
  isBureau?: boolean;
  isIreland?: boolean;
};

const PaymentModelForm: React.FC<PaymentModelFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
  isBureau = false,
  isIreland = false,
}) => {
  const isUK = isBureau && !isIreland;
  const { messages } = useTypedIntl();
  const defaultPaymentModel = getAppConfig('paymentModels');
  const hasPayeFTH = some(
    defaultPaymentModel,
    (item) => item.value === PAYMENT_MODEL_ENUM.PAYE_FTH,
  );

  const [form] = Form.useForm<PaymentModelFormValues>();
  const watchValues = Form.useWatch([], form);

  const umbrellaHolidayPayMethodOptions = useMemo(
    () =>
      getUmbrellaHolidayPaymentMethodOptions(
        watchValues?.umbrellaHolidayPayMethod?.value,
      ),
    [watchValues?.umbrellaHolidayPayMethod?.value],
  );

  // Ireland PAYE keeps Retained/Advanced/No Holiday Pay and gains the 8%
  // irregular method (NX-6284); only 52 Week Average is closed to Ireland.
  const payeHolidayPayMethodOptions = useMemo(
    () => getHolidayPaymentMethodOptions(isIreland, watchValues?.holidayPayMethod?.value),
    [isIreland, watchValues?.holidayPayMethod?.value],
  );

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  useEffect(() => {
    if (isUK) {
      form.setFieldValue('includedPaye', true);
    }
  }, [isUK]);

  // NX-6327: apply the 52 Week Average defaults only when the user picks it.
  // onValuesChange skips setFieldsValue/initialValues, so saved values survive load.
  const onValuesChange = (changed: any) => {
    if (
      changed?.holidayPayMethod?.value ===
      HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE
    ) {
      const allEarnings = defaultHolidayPayCalcOptions.find(
        (opt) => opt.value === 'ALL_EARNINGS',
      );
      if (allEarnings) {
        form.setFieldValue('holidayPayCalculationRule', allEarnings);
      }
      form.setFieldValue('includeInTimesheetRate', true);
    }
  };

  const onFinish = (values: PaymentModelFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={onValuesChange}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      {...(defaultValues ? { initialValues: defaultValues } : {})}
    >
      {/* PAYE */}
      <div>
        <Form.Item
          label={null}
          name="includedPaye"
          valuePropName="checked"
          labelCol={{ span: 0 }}
        >
          <AppCheckbox disabled={isUK}>{paymentModels.PAYEE}</AppCheckbox>
        </Form.Item>
        {watchValues?.includedPaye && (
          <>
            <Row gutter={[16, 0]}>
              <Col sm={24} md={12}>
                <div className="flex items-end gap-x-[8px]">
                  <div className="flex-1">
                    <Form.Item
                      label={messages['common.payslipTemplate']}
                      name="payePayslipTemplate"
                      className="mb-0"
                    >
                      <AppSelect
                        options={PAYMENT_MODEL_TEMPLATES?.PAYE}
                        placeholder={messages['form.select']}
                      />
                    </Form.Item>
                  </div>
                  {!!watchValues?.payePayslipTemplate?.value && (
                    <div
                      className="cursor-pointer mb-[12px]"
                      onClick={() =>
                        window.open(
                          getDemoTemplateLayout(
                            watchValues?.payePayslipTemplate?.value || '',
                            PAYMENT_MODEL_ENUM.PAYEE,
                            isIreland,
                          ),
                          '_blank',
                        )
                      }
                    >
                      <Icons.IconEye />
                    </div>
                  )}
                </div>
              </Col>
            </Row>
            {!isBureau && (
              <>
                <div className="text-[14px] font-bold mb-4 mt-4">
                  {messages['common.holidayPaySettings']}
                </div>
                <Row gutter={[16, 0]}>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={messages['common.holidayPay']}
                      name="enableHolidayPay"
                    >
                      <AppRadioGroup options={yesNoOptions} />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={`${messages['common.holidayPayRate']} %`}
                      name="payeHolidayPayRate"
                    >
                      <AppPercentageInput
                        placeholder={messages['form.enter']}
                        disabled={!watchValues?.enableHolidayPay}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={messages['common.holidayPayMethod']}
                      name="holidayPayMethod"
                    >
                      <AppSelect
                        options={payeHolidayPayMethodOptions}
                        placeholder={messages['form.select']}
                        disabled={!watchValues?.enableHolidayPay}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={messages['common.holidayPayCalculationRule']}
                      name="holidayPayCalculationRule"
                    >
                      <AppSelect
                        options={defaultHolidayPayCalcOptions}
                        placeholder={messages['form.select']}
                        disabled={!watchValues?.enableHolidayPay}
                      />
                    </Form.Item>
                  </Col>
                  {watchValues?.holidayPayMethod &&
                    watchValues.holidayPayMethod.value !==
                      HolidayMethodEnum.NO_HOLIDAY_PAY && (
                      <Col sm={24} md={12}>
                        <Form.Item
                          label={messages['common.includedTimesheetRate']}
                          name="includeInTimesheetRate"
                        >
                          <AppRadioGroup
                            options={yesNoOptions}
                            disabled={!watchValues?.enableHolidayPay}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  {/* NX-6284: Ireland-only leave year start, default 1
                      April — sits with the PAYE Holiday Pay Method it
                      feeds. Non-bureau only; bureau Ireland sets this in
                      the Payroll Setting tab instead. One searchable
                      dropdown over every calendar date (29 Feb excluded). */}
                  {isIreland && (
                    <Col sm={24} md={12}>
                      <Form.Item
                        label={
                          messages['common.holidayLeaveYearStart'] ??
                          'Leave Year Start'
                        }
                        name="holidayLeaveYearStart"
                      >
                        <AppSelect
                          options={leaveYearStartDateOptions}
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </>
            )}
          </>
        )}
      </div>
      {!isBureau && <Divider />}

      {/* PAYE Hybrid - Umbrella only, and not offered in Ireland (NX-5228) */}
      {!isBureau && !isIreland && (
        <>
          <div>
            <Form.Item
              label={null}
              name="includedOPayehybrid"
              valuePropName="checked"
              labelCol={{ span: 0 }}
            >
              <AppCheckbox>{paymentModels.PAYE_HYBRID}</AppCheckbox>
            </Form.Item>
            {watchValues?.includedOPayehybrid && (
              <Row gutter={[16, 0]}>
                <Col sm={24} md={12}>
                  <div className="flex items-end gap-x-[8px]">
                    <div className="flex-1">
                      <Form.Item
                        label={messages['common.payslipTemplate']}
                        name="hybridPayePayslipTemplate"
                        className="mb-0"
                      >
                        <AppSelect
                          options={PAYMENT_MODEL_TEMPLATES?.PAYE_HYBRID}
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </div>
                    {!!watchValues?.hybridPayePayslipTemplate?.value && (
                      <div
                        className="cursor-pointer mb-[12px]"
                        onClick={() =>
                          window.open(
                            getDemoTemplateLayout(
                              watchValues?.hybridPayePayslipTemplate?.value ||
                                '',
                              PAYMENT_MODEL_ENUM.PAYE_HYBRID,
                              isIreland,
                            ),
                            '_blank',
                          )
                        }
                      >
                        <Icons.IconEye />
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </div>
          <Divider />
        </>
      )}

      {/* CIS / RCT — Umbrella always, Bureau only for Ireland */}
      {(!isBureau || isIreland) && (
        <div>
          <Form.Item
            label={null}
            name="includedCis"
            valuePropName="checked"
            labelCol={{ span: 0 }}
          >
            <AppCheckbox>{isIreland ? 'RCT' : paymentModels.CIS}</AppCheckbox>
          </Form.Item>
          {watchValues?.includedCis && (
            <Row gutter={[16, 0]}>
              <Col sm={24} md={12}>
                <div className="flex items-end gap-x-[8px]">
                  <div className="flex-1">
                    <Form.Item
                      label={messages['common.payslipTemplate']}
                      name="cisPayslipTemplate"
                      className="mb-0"
                    >
                      <AppSelect
                        options={PAYMENT_MODEL_TEMPLATES?.CIS}
                        placeholder={messages['form.select']}
                      />
                    </Form.Item>
                  </div>
                  {!!watchValues?.cisPayslipTemplate?.value && (
                    <div
                      className="cursor-pointer mb-[12px]"
                      onClick={() =>
                        window.open(
                          isIreland
                            ? RCT_DEFAULT_DEMO_LAYOUT
                            : getDemoCisTemplateLayout(
                                watchValues?.cisPayslipTemplate?.value || '',
                                SELF_CIS_DEFAULT_DEMO_LAYOUT,
                              ),
                          '_blank',
                        )
                      }
                    >
                      <Icons.IconEye />
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </div>
      )}
      {!isBureau && <Divider />}

      {/* PAYE FTH - Umbrella only, feature-flagged via app config, not offered in Ireland (NX-5228) */}
      {!isBureau && !isIreland && hasPayeFTH && (
        <>
          <div>
            <Form.Item
              label={null}
              name="includedOPayeFth"
              valuePropName="checked"
              labelCol={{ span: 0 }}
            >
              <AppCheckbox>{paymentModels.PAYE_FTH}</AppCheckbox>
            </Form.Item>
            {watchValues?.includedOPayeFth && (
              <Row gutter={[16, 0]}>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.taxFreePayment']}
                    name="taxFreePaymentDesc"
                    rules={[
                      {
                        required: true,
                        message: 'Tax Free Payment Description is required!',
                      },
                    ]}
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <div className="flex items-end gap-x-[8px]">
                    <div className="flex-1">
                      <Form.Item
                        label={messages['common.payslipTemplate']}
                        name="payeETHPayslipTemplate"
                        className="mb-0"
                      >
                        <AppSelect
                          options={PAYMENT_MODEL_TEMPLATES?.PAYE_FTH}
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </div>
                    {!!watchValues?.payeETHPayslipTemplate?.value && (
                      <div
                        className="cursor-pointer mb-[12px]"
                        onClick={() =>
                          window.open(
                            getDemoTemplateLayout(
                              watchValues?.payeETHPayslipTemplate?.value || '',
                              PAYMENT_MODEL_ENUM.PAYE_FTH,
                              isIreland,
                            ),
                            '_blank',
                          )
                        }
                      >
                        <Icons.IconEye />
                      </div>
                    )}
                  </div>
                </Col>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={`${messages['common.marginUmbrellaAdvanceAdjustment']}?`}
                    name="payeFthMarginAdjustment"
                  >
                    <AppRadioGroup options={yesNoOptions} />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </div>
          <Divider />
        </>
      )}

      {/* Umbrella is an Umbrella-product model in every country. The RCT card
          above is the one Ireland adds to bureau. */}
      {!isBureau && (
        <>
          {/* Umbrella - Umbrella only */}
          <div>
            <Form.Item
              label={null}
              name="includedUmbrella"
              valuePropName="checked"
              labelCol={{ span: 0 }}
            >
              <AppCheckbox>{paymentModels.UMBRELLA}</AppCheckbox>
            </Form.Item>
            {watchValues?.includedUmbrella && (
              <>
                <div className="text-[14px] font-bold mb-4">
                  {messages['common.processingRules']}
                </div>
                <Row gutter={[16, 0]}>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={messages['common.splitSalaryAndBonus']}
                      name="splitSalaryAndBonus"
                    >
                      <AppRadioGroup options={yesNoOptions} />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <div className="flex items-end gap-x-[8px]">
                      <div className="flex-1">
                        <Form.Item
                          label={messages['common.payslipTemplate']}
                          name="umbrellaPayslipTemplate"
                          className="mb-0"
                        >
                          <AppSelect
                            options={
                              // NX-5228: Ireland has one umbrella template;
                              // offering Layout 2/3 would preview UK layouts.
                              isIreland
                                ? [PAYSLIP_TEMPLATES.DEFAULT]
                                : PAYMENT_MODEL_TEMPLATES?.UMBRELLA
                            }
                            placeholder={messages['form.select']}
                          />
                        </Form.Item>
                      </div>
                      {/* NX-5228: no Ireland-Umbrella preview asset yet
                          (phase 7) — getDemoTemplateLayout would fall through
                          to the UK Umbrella demo, misleading for Ireland. */}
                      {!!watchValues?.umbrellaPayslipTemplate?.value &&
                        !isIreland && (
                          <div
                            className="cursor-pointer mb-[12px]"
                            onClick={() =>
                              window.open(
                                getDemoTemplateLayout(
                                  watchValues?.umbrellaPayslipTemplate
                                    ?.value || '',
                                  PAYMENT_MODEL_ENUM.UMBRELLA,
                                  isIreland,
                                ),
                                '_blank',
                              )
                            }
                          >
                            <Icons.IconEye />
                          </div>
                        )}
                    </div>
                  </Col>
                </Row>
                <div className="text-[14px] font-bold mb-4">
                  {messages['common.holidayPaySettings']}
                </div>
                <Row gutter={[16, 0]}>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={messages['common.holidayPay']}
                      name="enableHolidayPayUmbrella"
                    >
                      <AppRadioGroup options={yesNoOptions} />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={`${messages['common.holidayPayRate']} %`}
                      name="umbrellaHolidayPayRate"
                    >
                      <AppPercentageInput
                        placeholder={messages['form.enter']}
                        disabled={!watchValues?.enableHolidayPayUmbrella}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={messages['common.holidayPayMethod']}
                      name="umbrellaHolidayPayMethod"
                    >
                      <AppSelect
                        options={umbrellaHolidayPayMethodOptions}
                        placeholder={messages['form.select']}
                        disabled={!watchValues?.enableHolidayPayUmbrella}
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={12}>
                    <Form.Item
                      label={messages['common.holidayPayCalculationRule']}
                      name="holidayPayCalculationRule"
                    >
                      <AppSelect
                        options={defaultHolidayPayCalcOptions}
                        placeholder={messages['form.select']}
                        disabled={!watchValues?.enableHolidayPayUmbrella}
                      />
                    </Form.Item>
                  </Col>
                  {/* NX-5228: App Levy / Accrue Employment Costs have no
                      Ireland equivalent — UK-umbrella-tenant only. */}
                  {!isIreland && (
                    <>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label={messages['common.reclaimAppLevyCosts']}
                          name="reclaimAppLevyUmbrella"
                        >
                          <AppRadioGroup options={yesNoOptions} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label={messages['common.accrueEmploymentCosts']}
                          name="accrueEmploymentCosts"
                        >
                          <AppRadioGroup options={yesNoOptions} />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
              </>
            )}
          </div>
          {/* NX-5228: Umbrella Hybrid stays a UK-umbrella-tenant feature. */}
          {!isIreland && (
            <>
          <Divider />

          {/* Umbrella Hybrid - Umbrella only */}
          <div>
            <Form.Item
              label={null}
              name="includedHybrid"
              valuePropName="checked"
              labelCol={{ span: 0 }}
            >
              <AppCheckbox>{paymentModels.HYBRID}</AppCheckbox>
            </Form.Item>
            {watchValues?.includedHybrid && (
              <Row gutter={[16, 0]}>
                <Col sm={24} md={12}>
                  <Form.Item
                    label={messages['common.reclaimAppLevyCosts']}
                    name="reclaimAppLevyHybrid"
                  >
                    <AppRadioGroup options={yesNoOptions} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={12}>
                  <div className="flex items-end gap-x-[8px]">
                    <div className="flex-1">
                      <Form.Item
                        label={messages['common.payslipTemplate']}
                        name="hybridPayslipTemplate"
                        className="mb-0"
                      >
                        <AppSelect
                          options={PAYMENT_MODEL_TEMPLATES?.HYBRID}
                          placeholder={messages['form.select']}
                        />
                      </Form.Item>
                    </div>
                    {!!watchValues?.hybridPayslipTemplate?.value && (
                      <div
                        className="cursor-pointer mb-[12px]"
                        onClick={() =>
                          window.open(
                            getDemoTemplateLayout(
                              watchValues?.hybridPayslipTemplate?.value || '',
                              PAYMENT_MODEL_ENUM.HYBRID,
                            ),
                            '_blank',
                          )
                        }
                      >
                        <Icons.IconEye />
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </div>
            </>
          )}
        </>
      )}
    </AppFormContainer>
  );
};

export default PaymentModelForm;
